'use client'

import { useEffect } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
    PaginationParams,
    PaginationQueryParams,
    addPaginationQueryParamsToPath,
    extractQueryParamsForPagination,
    toPaginationParams,
} from '../../../MyStichtingOpenHelpers/src/static'

interface PaginationDefaults {
    initialPage: number
    limit: number
}

interface UseInitialPaginationFromURLProps extends PaginationDefaults {
    bindToURL: boolean
    offset: number
}

interface UsePaginationQueryParamsInURLProps extends PaginationDefaults {
    bindToURL: boolean
    page: number
    perPage: number
}

interface InitialPagination {
    page: number
    params: PaginationParams
}

const getDefaults = ({ initialPage, limit }: PaginationDefaults): PaginationQueryParams => ({
    page: initialPage,
    perPage: limit,
})

/**
 * Reads the pagination state a paginated overview should start with. Falls back to the offset and
 * limit it was configured with when the state is not kept in the URL.
 */
export const useInitialPaginationFromURL = ({
    bindToURL,
    offset,
    limit,
    initialPage,
}: UseInitialPaginationFromURLProps): InitialPagination => {
    const searchParams = useSearchParams()

    if (!bindToURL) {
        return { page: initialPage, params: { offset, limit } }
    }

    const pagination = extractQueryParamsForPagination(searchParams, getDefaults({ initialPage, limit }))
    return { page: pagination.page, params: toPaginationParams(pagination) }
}

/**
 * Keeps the browser URL in step with the current page and page size so the position survives a
 * refresh or a shared link. Params that equal their default are left out to keep the URL clean.
 */
export const usePaginationQueryParamsInURL = ({
    bindToURL,
    initialPage,
    limit,
    page,
    perPage,
}: UsePaginationQueryParamsInURLProps): void => {
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const router = useRouter()
    const defaults = getDefaults({ initialPage, limit })

    useEffect(() => {
        if (!bindToURL) return
        const currentQuery = searchParams.toString()
        const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname
        const nextUrl = addPaginationQueryParamsToPath(currentUrl, { page, perPage }, defaults)
        // Replacing changes searchParams, which re-runs this effect. Bailing out on an unchanged
        // url is what makes it settle instead of looping.
        if (nextUrl === currentUrl) return
        router.replace(nextUrl, { scroll: false })
    }, [bindToURL, page, perPage, pathname, searchParams])
}
